import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import { getUserErrorMessage, restaurantForms } from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import type { z } from 'zod';
import { ActionButton } from '@/components/ActionButton';
import { FormWrapper } from '@/components/FormWrapper';
import { MultiSelect } from '@/components/MultiSelect';
import { NullableInput } from '@/components/NullableInput';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { handleSubmitErrors } from '@/lib/handleSubmitErrors';
import {
  type Menu,
  type Restaurant,
  useMenu,
  useRestaurant,
} from '@/routes/restaurant/useRestaurantData';

const LOYALTY_CARD_MIN = 2;
const formValuesSchema = restaurantForms.loyaltyCard;
type FormValues = z.output<typeof formValuesSchema>;

const toFormValues = (restaurant: Restaurant, menu: Menu | null): FormValues => ({
  loyaltyCardEnabled: restaurant.loyaltyCardEnabled ?? false,
  loyaltyCardTarget: restaurant.loyaltyCardTarget ?? LOYALTY_CARD_MIN,
  categoryUuids: (menu?.categories ?? []).filter((c) => !!c.loyaltyCardEligible).map((c) => c.uuid),
});

export const Route = createFileRoute('/r/$restaurantId/s/loyalty-card')({
  component: LoyaltyCardPage,
});

function LoyaltyCardPage() {
  const convex = useConvex();
  const { restaurantId } = Route.useParams();
  const restaurant = useRestaurant();
  const menu = useMenu();

  const form = useForm({
    defaultValues: toFormValues(restaurant, menu),
    resolver: zodResolver(formValuesSchema),
  });

  const loyaltyCardEnabled = useWatch({ control: form.control, name: 'loyaltyCardEnabled' });
  const loyaltyCardTarget = useWatch({ control: form.control, name: 'loyaltyCardTarget' });

  // Subscribe to changes
  useEffect(() => {
    // Ensure un-saved user changes are kept on Convex sync
    form.reset(toFormValues(restaurant, menu), { keepDirty: true, keepDirtyValues: true });
  }, [form, restaurant, menu]);

  const onValidSubmit = async (formValues: FormValues) => {
    try {
      await convex.mutation(api.restaurants.saveLoyaltyCardSetting, {
        formValues,
        restaurantId,
      });
      // The saved values are the new baseline, so nothing is dirty any more
      form.reset(form.getValues());
      toast.success('Saved successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Error saving!', { description });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Loyalty Card</CardTitle>
        <CardDescription>
          Allow customers to earn a free coffee after purchasing a certain number of coffees
        </CardDescription>
      </CardHeader>
      <CardContent>
        <FormWrapper {...form}>
          <form
            id="form"
            onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
            className="grid gap-6"
          >
            {/* loyaltyCardEnabled (boolean) */}
            <FormField
              control={form.control}
              name="loyaltyCardEnabled"
              render={({ field }) => (
                <FormItem className="w-1/2 flex flex-row items-center justify-between rounded-lg border p-4">
                  <FormControl>
                    <div className="flex items-center space-x-2">
                      <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                      <FormLabel>Enabled</FormLabel>
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />

            {/* loyaltyCardTarget (number) */}
            {loyaltyCardEnabled && (
              <FormField
                control={form.control}
                name="loyaltyCardTarget"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Loyalty Stamp Target</FormLabel>
                    <FormControl>
                      <NullableInput
                        {...field}
                        className="w-24"
                        max={9}
                        min={LOYALTY_CARD_MIN}
                        onChange={(value) =>
                          value
                            ? form.setValue(field.name, +value, { shouldDirty: true })
                            : form.unregister(field.name)
                        }
                        type={'number'}
                      />
                    </FormControl>
                    <FormDescription>
                      Order {loyaltyCardTarget} coffees, get 1 free!
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="categoryUuids"
              render={({ field }) => (
                <FormItem className={'md:w-3/4'}>
                  <FormLabel>Loyalty Categories</FormLabel>
                  <FormControl>
                    <MultiSelect
                      onValuesChange={field.onChange}
                      options={(menu?.categories ?? []).map((category) => ({
                        label: category.name,
                        value: category.uuid,
                      }))}
                      placeholder="Select categories"
                      values={field.value}
                    />
                  </FormControl>
                  <FormDescription>
                    Any menu item from the selected categories will earn a loyalty stamp.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </FormWrapper>
      </CardContent>
      <CardFooter className="border-t">
        <ActionButton
          disabled={form.formState.isSubmitting || !form.formState.isDirty || !menu}
          loading={form.formState.isSubmitting}
          type="submit"
          form="form"
        >
          Save
        </ActionButton>
      </CardFooter>
    </Card>
  );
}
